import { Link, useLocation, useRouter } from "@tanstack/react-router";
import {
	LayoutGroup,
	motion,
	useMotionValueEvent,
	useScroll,
	useSpring,
} from "motion/react";
import { useEffect, useId, useRef, useState } from "react";
import { LogoMark } from "@/components/brand/LogoMark";
import { EASE } from "@/components/motion/motion";
import { Button } from "@/components/ui/button";
import { ANALYTICS_EVENTS, useAnalytics } from "@/lib/analytics";
import { SCHEDULE_VISIO_URL } from "@/lib/constants";
import {
	buildLocalizedPath,
	type Locale,
	setLocaleCookie,
	stripLocaleFromPathname,
} from "@/lib/i18n/locale";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

type NavLink = {
	label: string;
	to:
		| "/{-$locale}/expo-migration"
		| "/{-$locale}/questovery"
		| "/{-$locale}/about"
		| "/{-$locale}/blog"
		| "/{-$locale}/cv";
	match: string;
};

export default function Header() {
	const router = useRouter();
	const { t, locale, localeParam } = useI18n();
	const { capture } = useAnalytics();
	const mobileMenu = useRef<HTMLDetailsElement>(null);
	const [scrolled, setScrolled] = useState(false);
	const [hidden, setHidden] = useState(false);
	const [hovered, setHovered] = useState<string | null>(null);
	const navGroup = useId();
	const { scrollY, scrollYProgress } = useScroll();
	const progress = useSpring(scrollYProgress, {
		stiffness: 260,
		damping: 40,
		restDelta: 0.001,
	});

	// Tuck the header away while reading down, bring it back on the way up.
	useMotionValueEvent(scrollY, "change", (y) => {
		const previous = scrollY.getPrevious() ?? 0;
		const menuOpen = mobileMenu.current?.open ?? false;
		if (y < 120 || menuOpen) setHidden(false);
		else if (y > previous + 4) setHidden(true);
		else if (y < previous - 4) setHidden(false);
	});
	const localeParams: Record<string, string> = localeParam
		? { locale: localeParam }
		: {};
	const pathname = useLocation({ select: (location) => location.pathname });
	const basePathname = stripLocaleFromPathname(pathname);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 24);
		onScroll();
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape" && mobileMenu.current?.open) {
				mobileMenu.current.open = false;
			}
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("keydown", onKeyDown);
		return () => {
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("keydown", onKeyDown);
		};
	}, []);

	const links: NavLink[] = [
		{
			label: t((t) => t.nav.expoMigration),
			to: "/{-$locale}/expo-migration",
			match: "/expo-migration",
		},
		{
			label: t((t) => t.nav.questovery),
			to: "/{-$locale}/questovery",
			match: "/questovery",
		},
		{ label: t((t) => t.nav.about), to: "/{-$locale}/about", match: "/about" },
		{ label: t((t) => t.nav.blog), to: "/{-$locale}/blog", match: "/blog" },
		{ label: t((t) => t.nav.cv), to: "/{-$locale}/cv", match: "/cv" },
	];

	const closeMenu = () => {
		if (mobileMenu.current) mobileMenu.current.open = false;
	};

	const switchLocale = (nextLocale: Locale) => {
		closeMenu();
		if (nextLocale === locale) return;
		capture(ANALYTICS_EVENTS.localeSwitch, {
			from_locale: locale,
			to_locale: nextLocale,
			placement: "header",
		});
		setLocaleCookie(nextLocale);
		router.history.push(
			buildLocalizedPath(
				router.state.location.pathname,
				router.state.location.searchStr,
				router.state.location.hash,
				nextLocale,
			),
		);
	};

	const bookCall = (placement: string) =>
		capture(ANALYTICS_EVENTS.ctaClick, {
			cta: "schedule_call",
			placement,
			href: SCHEDULE_VISIO_URL,
		});

	const activeMatch = links.find((link) =>
		basePathname.startsWith(link.match),
	)?.match;

	/** Desktop: one pastel bar that slides to the hovered link and back to the page's. */
	const renderLinks = (className: string, sliding = false) =>
		links.map((link) => {
			const active = link.match === activeMatch;
			const marked = sliding ? (hovered ?? activeMatch) === link.match : active;
			return (
				<Link
					key={link.to}
					to={link.to}
					params={localeParams}
					onClick={closeMenu}
					onMouseEnter={sliding ? () => setHovered(link.match) : undefined}
					onFocus={sliding ? () => setHovered(link.match) : undefined}
					onBlur={sliding ? () => setHovered(null) : undefined}
					aria-current={active ? "page" : undefined}
					className={cn(
						className,
						"relative transition-colors hover:text-ink",
						active ? "text-ink" : "text-ink-2",
					)}
				>
					{link.label}
					{marked && sliding ? (
						<motion.span
							layoutId="nav-mark"
							className={cn(
								"pastel-bar absolute right-0 -bottom-1 left-0 h-[3px]",
								!active && "opacity-60",
							)}
							transition={{ type: "spring", stiffness: 420, damping: 34 }}
							aria-hidden="true"
						/>
					) : null}
					{marked && !sliding ? (
						<span
							className="pastel-bar absolute right-0 -bottom-1 left-0 h-[3px]"
							aria-hidden="true"
						/>
					) : null}
				</Link>
			);
		});

	const localeSwitcher = (
		<div className="flex items-center gap-1 font-mono text-[0.78rem]">
			{(["fr-FR", "en-US"] as const).map((option, i) => (
				<span key={option} className="flex items-center gap-1">
					{i > 0 ? (
						<span className="text-ink-4" aria-hidden="true">
							/
						</span>
					) : null}
					<button
						type="button"
						onClick={() => switchLocale(option)}
						aria-pressed={locale === option}
						aria-label={option === "fr-FR" ? "Français" : "English"}
						lang={option.slice(0, 2)}
						className={cn(
							"px-0.5 uppercase transition-colors",
							locale === option
								? "text-ink underline underline-offset-4"
								: "text-ink-3 hover:text-ink",
						)}
					>
						{option.slice(0, 2)}
					</button>
				</span>
			))}
		</div>
	);

	return (
		<motion.header
			className={cn(
				"site-header sticky top-0 z-50 border-b border-dashed print:hidden bg-paper/92 backdrop-blur-[3px] transition-colors duration-300",
				scrolled ? "border-rule-2" : "border-transparent",
			)}
			animate={{ y: hidden ? "-100%" : "0%" }}
			transition={{ duration: 0.45, ease: EASE }}
			onFocusCapture={() => setHidden(false)}
		>
			<motion.span
				className="pastel-bar absolute right-0 -bottom-px left-0 h-[2px] origin-left"
				style={{ scaleX: progress }}
				aria-hidden="true"
			/>
			<div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-6 px-5 md:px-10">
				<Link
					to="/{-$locale}"
					params={localeParams}
					className="flex items-center gap-2.5 font-dots text-[1.35rem] leading-none font-extrabold tracking-tight text-ink"
				>
					<LogoMark print className="size-7" />
					{t((t) => t.nav.brand)}
				</Link>

				<LayoutGroup id={navGroup}>
					<nav
						aria-label={t((t) => t.nav.menu)}
						className="hidden items-center gap-6 font-mono text-[0.82rem] lg:flex"
						onMouseLeave={() => setHovered(null)}
					>
						{renderLinks("py-1", true)}
					</nav>
				</LayoutGroup>

				<div className="hidden items-center gap-5 lg:flex">
					{localeSwitcher}
					<Button asChild size="sm">
						<a
							href={SCHEDULE_VISIO_URL}
							target="_blank"
							rel="noreferrer"
							onClick={() => bookCall("header")}
						>
							{t((t) => t.nav.bookCall)}
						</a>
					</Button>
				</div>

				<details ref={mobileMenu} className="group lg:hidden">
					<summary className="cursor-pointer list-none border border-rule-2 px-3 py-1.5 font-mono text-[0.78rem] text-ink [&::-webkit-details-marker]:hidden">
						{t((t) => t.nav.menu)}
						<span
							className="ml-1.5 inline-block transition-transform group-open:rotate-45"
							aria-hidden="true"
						>
							+
						</span>
					</summary>
					<div className="menu-drop absolute inset-x-0 top-16 border-y border-dashed border-rule-2 bg-paper px-5 pt-4 pb-6 shadow-[0_12px_24px_-18px_rgba(42,42,48,0.35)]">
						<nav
							aria-label={t((t) => t.nav.menu)}
							className="flex flex-col font-mono text-base"
						>
							{renderLinks(
								"menu-item border-b border-dotted border-rule-2 py-3",
							)}
						</nav>
						<div className="mt-5 flex items-center justify-between gap-4">
							{localeSwitcher}
							<Button asChild size="sm">
								<a
									href={SCHEDULE_VISIO_URL}
									target="_blank"
									rel="noreferrer"
									onClick={() => {
										closeMenu();
										bookCall("header_mobile");
									}}
								>
									{t((t) => t.nav.bookCall)}
								</a>
							</Button>
						</div>
					</div>
				</details>
			</div>
		</motion.header>
	);
}
